import React from "react"

import {
	Box,
	Button,
	Grid,
	Typography
} from "@mui/material"

import { Trans } from "@lingui/macro"
import debounce from "lodash/debounce"

import {
	AssetMatchBrandWithImage, AssetProvenience,
	AssetWithImage,
	BrandPriority,
	SearchMatchRequest,
	SessionResponse
} from "models"

import { DashboardMapSearchForm } from "./dashboard-map-search-form"

import MatchProposalItemBrand from "components/match-proposal/match-proposal-list-brand/match-proposal-item-brand"
import { Link } from "react-router-dom"
import "./dashboard-map.scss"


export interface DashboardMapProps {
	hideTitle?: boolean
	assetList?: AssetWithImage[] | null  // admin
	matches?: AssetMatchBrandWithImage[] | null    // brand
	onMapBoundsChange?: (bounds: google.maps.LatLngBounds, priority: BrandPriority | null) => void
	brandPrioritySelection?: (priority: BrandPriority | null, brandId: string) => void
	sessionData: SessionResponse
	likes: string[]
	updateLikes?: (likes: string[]) => void
	checkedAssets?: string[]
	provenience?: AssetProvenience | null
	enableMerge?: (checkedAssets: string[], provenience: AssetProvenience | null) => void
	assetPage?: boolean
	searchMatch?: (request: SearchMatchRequest, id: string) => void
	clearForm?: () => void
	openUploadDocument?: () => void
	reloadPriorities?: boolean
	selectedPriority?: BrandPriority | null
	currentBrand?: string
	onChangePagination: (_: any, value: number) => void
	pageId: number
	totalPages: number
	onBrandChange: (brand: string) => void
}
interface DashboardMapState {
	loading: boolean
	center: google.maps.LatLngLiteral
	mapInitialised: boolean
	brandId: string | null
	brandPriority: BrandPriority | null
	mapUpdatedByPriority: boolean
	mapKey: number
}
class DashboardMap extends React.Component<DashboardMapProps, DashboardMapState> {
	static defaultProps = {
		hideTitle: false,
		reloadPriorities: false
	}

	private readonly MAP_CENTER_LAT: number = 42
	private readonly MAP_CENTER_LONG: number = 12

	private mapRef: any = React.createRef();

	constructor(props: any) {
		super(props)

		this.state = {
			center: { lat: this.MAP_CENTER_LAT, lng: this.MAP_CENTER_LONG },
			loading: true,
			mapInitialised: false,
			brandId: this.props.currentBrand!,
			brandPriority: null,
			mapUpdatedByPriority: false,
			mapKey: 0
		}
	}

	private handleBoundsChanged = debounce((): void => {
		if (!this.state.mapInitialised) {
			return
		}
		if (this.state.mapUpdatedByPriority) {
			return
		}

		const bounds = this.mapRef.getBounds()
		this.props.onMapBoundsChange?.(bounds, this.state.brandPriority)
	}, 100)

	private onBrandChange = (brandId?: string): void => {
		/* this.setState(
			() => ({
				center: {
					lat: this.MAP_CENTER_LAT,
					lng: this.MAP_CENTER_LONG,
				},
				brandId: brandId ?? null,
				brandPriority: null,
			}),
			() => {
				if (this.state.mapInitialised) {
					this.mapRef.setZoom(6)
				}
			}
		) */
		if (this.props.currentBrand !== brandId) {
			this.props.onBrandChange(brandId!)
		}
	}

	private getAssetsForMarkers = (): AssetWithImage[] => {
		if (this.props.assetList) {
			let assetWithLocation: AssetWithImage[] = []
			this.props.assetList.map((item) => {
				if (item.asset.location) {
					assetWithLocation.push(item)
				}
			})
			return assetWithLocation
		}

		const assets = this.props.matches?.filter(
			(it: AssetMatchBrandWithImage, index: number, array) => array.indexOf(it) === index
		)
			.map(it => {
				return { asset: it.match.asset, image: it.image } as AssetWithImage
			})

		return assets ?? []
	}

	private getMapBounds = (): google.maps.LatLngBounds | null => {
		const assetsForMarkers = this.getAssetsForMarkers()
		if (assetsForMarkers.length === 0) {
			return null
		}

		const bounds = new google.maps.LatLngBounds()
		assetsForMarkers
			.map((asset: AssetWithImage) => asset.asset.location.coordinates)
			.forEach((location: number[]) => {
				bounds.extend(new google.maps.LatLng(location[1], location[0]))
			})

		return bounds
	}
	
	private onSearch = (brandPriority?: BrandPriority): void => {
		this.setState(() => ({
			center: {
				lat: this.MAP_CENTER_LAT,
				lng: this.MAP_CENTER_LONG,
			},
			brandPriority: brandPriority ?? null
		}), () => {
			if (this.props.brandPrioritySelection) {
				this.props.brandPrioritySelection(this.state.brandPriority, this.state.brandId!)
			}
			if (this.state.mapInitialised) {
				this.mapRef.setZoom(6)
			}
			this.handleBoundsChanged()
		})
	}

	render() {
		const profile = this.props.sessionData.accountType

		const isListEmpty: boolean = ((this.props.assetList?.length ?? 0) === 0) &&
			((this.props.matches?.length ?? 0) === 0)

		return (
			<Box sx={{ backgroundColor: 'background.default' }} py={2}>
				{!this.props.hideTitle &&
					<Box sx={{ clear: "auto" }}>
						<Box sx={{ display: "flex", textAlign: 'left', mb: 2, float: "left", width: "50%" }}>
							<Typography fontSize={16} fontWeight="light">
								<Trans id="dm-title">Distribuzione geografica degli immobili</Trans>
							</Typography>
						</Box>
						<Box sx={{ display: "flex", mb: 2, width: "50%", justifyContent: "right" }}>
							<Link to="/asset" style={{ textDecoration: "none" }}>
								<Button variant="contained"
									type="button"
									disableElevation
									sx={{
										textTransform: "none",
										borderColor: "primary.main",
										bgcolor: "primary.main",
										paddingY: 1,
										paddingX: 2
									}}
								>
									<Trans id={"pages.titles.asset-list"}>

									</Trans>
								</Button>
							</Link>
						</Box>
					</Box>
				}

				<Grid container spacing={2}>
					{profile === "BRAND" &&
						<Grid item xs={12}>
							<DashboardMapSearchForm
								onBrandChange={this.onBrandChange}
								sessionData={this.props.sessionData}
								onSearch={this.onSearch}
								search={this.props.searchMatch}
								clear={this.props.clearForm}
								openUploadDocument={this.props.openUploadDocument}
								reloadPriorities={this.props.reloadPriorities}
								selectedPriority={this.props.selectedPriority}
								selectedBrand={this.state.brandId}
							/>
						</Grid>
					}

					<Grid item md={12} sx={{ height: "600px", overflowY: "scroll", mt: 2 }}>
						{/* {this.props.assetList?.map((item: AssetWithImage) =>
							<DashboardMapStores key={item.asset.id}
												asset={item}
												likes={this.props.likes}
												sessionData={this.props.sessionData}
												updateLikes={this.props.updateLikes}
												checkedAssets={this.props.checkedAssets}
												provenience={this.props.provenience}
												enableMerge={this.props.enableMerge}
												assetPage={this.props.assetPage}
							/>
						)}
						{this.props.matches?.map((item: AssetMatchBrandWithImage) =>
							<DashboardMapStores key={item.match.id}
												match={item}
												likes={this.props.likes}
												sessionData={this.props.sessionData}
												updateLikes={this.props.updateLikes}
							/>
						)} */}

						{this.props.matches?.map((item: AssetMatchBrandWithImage) =>
							<MatchProposalItemBrand
								match={item}
							/>
						)}
						{isListEmpty &&
							<Typography sx={{ mt: 5, textAlign: "center" }}>Nessun risultato trovato</Typography>
						}
					</Grid>
				</Grid>
			</Box>
		)
	}
}

export default DashboardMap
